/**
 * Merge request previews of `@abax/ui`.
 *
 * The preload runs before sign-in, so it cannot read federation-config, which is
 * where shell-backend hands a gated user the preview's `remoteEntry.js`. The
 * signed `mf-manifest.json` next to that entry is remembered for this tab when
 * federation-config arrives, and the page reloads once so the next preload loads
 * it. Per tab. It is cleared on sign-out, when another user signs in to the tab
 * and before every redirect to the identity server, so the next user never boots
 * into it. When federation-config stops carrying the preview, the key is cleared
 * and the page reloads once more onto the stable build. A preview the preload cannot load or
 * verify is dropped for the tab, and the page reloads once onto the stable build.
 *
 * Only where the staging signing key is trusted and no commit pin is set: a
 * preview is signed with the staging key, so it could never verify anywhere
 * else, and a commit pin (a staging rollback) rejects every preview build.
 */
import { canonicalCdnIdentity } from '@abax/platform';
import { trustsStagingAbaxUiManifestKey } from '@/config/abax-ui-manifest-public-key';

const STORAGE_KEY = 'abax-shell-abax-ui-preview-manifest';
const RELOADED_KEY = 'abax-shell-abax-ui-preview-reloads';
const FAILED_KEY = 'abax-shell-abax-ui-preview-failed';
const FAILED_CLAIM = ' -> failed';

// 975 is the shell's GitLab project. The preview jobs upload to
// preview/<project>/<MR iid>/<pipeline iid>/, and shell-backend accepts
// @abax/ui only from this project (its MfePreview:PackageDeployers pin). Change both together.
const PREVIEW_MANIFEST_PATH_RE = /^preview\/975\/\d+\/\d+\/mf-manifest\.json$/;

export interface AbaxUiPreviewConfig {
  ENVIRONMENT: string | undefined;
  /** The stable signed manifest. A preview must come from the same CDN. */
  ABAX_UI_MANIFEST_URL: string;
  ABAX_UI_EXPECTED_COMMIT?: string;
}

function previewCdnBase(config: AbaxUiPreviewConfig): string | null {
  if (
    !config.ABAX_UI_MANIFEST_URL ||
    config.ABAX_UI_EXPECTED_COMMIT ||
    !trustsStagingAbaxUiManifestKey(config.ENVIRONMENT)
  ) {
    return null;
  }
  return canonicalCdnIdentity(config.ABAX_UI_MANIFEST_URL);
}

function asPreviewManifestUrl(url: string | null | undefined, cdnBase: string): string | null {
  if (!url) return null;
  let href: string;
  try {
    href = new URL(url).href;
  } catch {
    return null;
  }
  if (!href.startsWith(cdnBase)) return null;
  return PREVIEW_MANIFEST_PATH_RE.test(href.slice(cdnBase.length)) ? href : null;
}

function readStored(): string | null {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Unwritable storage: the read-back in syncAbaxUiPreview then skips the reload.
  }
}

function readList(key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

// The reload claim doubles as the mark when the failed list could not be written.
function hasFailed(url: string): boolean {
  return (
    readList(FAILED_KEY).includes(url) || readList(RELOADED_KEY).includes(`${url}${FAILED_CLAIM}`)
  );
}

/**
 * Records `transition` for this tab and reports whether it is new. A transition
 * that already reloaded this tab never reloads it again, so federation-config
 * alternating between two answers cannot loop.
 */
function claimReload(transition: string): boolean {
  const done = readList(RELOADED_KEY);
  if (done.includes(transition)) return false;
  try {
    window.sessionStorage.setItem(RELOADED_KEY, JSON.stringify([...done, transition]));
  } catch {
    return false;
  }
  return readList(RELOADED_KEY).includes(transition);
}

/**
 * Forget the remembered preview. The next user of this tab must not boot into
 * the previous user's preview before their own federation-config arrives.
 */
export function clearAbaxUiPreview(): void {
  writeStored(null);
  try {
    // Else the same user signing back in could not reload into the same preview. Failure
    // claims stay, since hasFailed reads them.
    const failures = readList(RELOADED_KEY).filter((t) => t.endsWith(FAILED_CLAIM));
    if (failures.length) window.sessionStorage.setItem(RELOADED_KEY, JSON.stringify(failures));
    else window.sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    // Unwritable storage: nothing was recorded either.
  }
}

/**
 * The remembered preview manifest the preload should load, or null for the stable one.
 * Where previews are off it also forgets the tab's preview.
 */
export function readAbaxUiPreviewManifestUrl(config: AbaxUiPreviewConfig): string | null {
  const cdnBase = previewCdnBase(config);
  if (!cdnBase) {
    // Kept under a commit pin, the pointer would load once the pin lifts, before federation-config
    // confirms it. Its reload record goes too, or the preview could not reload in again.
    if (readStored() !== null) clearAbaxUiPreview();
    return null;
  }
  const url = asPreviewManifestUrl(readStored(), cdnBase);
  return url && !hasFailed(url) ? url : null;
}

/**
 * Remembers or forgets the preview from `@abax/ui`'s federation-config entry.
 * Reloads through `reload` when the page did not load what it should, at most once
 * per change. `loadedManifestUrl` is the manifest the preload used.
 */
export function syncAbaxUiPreview(
  entryUrl: string,
  loadedManifestUrl: string | null,
  config: AbaxUiPreviewConfig,
  reload: () => void,
): void {
  const cdnBase = previewCdnBase(config);
  if (!cdnBase) return;

  let wanted: string | null;
  try {
    wanted = asPreviewManifestUrl(new URL('mf-manifest.json', entryUrl).href, cdnBase);
  } catch {
    wanted = null;
  }
  if (wanted && hasFailed(wanted)) wanted = null;
  if (readStored() !== wanted) writeStored(wanted);

  const loaded = asPreviewManifestUrl(loadedManifestUrl, cdnBase);
  if (loaded === wanted || readStored() !== wanted) return;
  if (claimReload(`${loaded ?? ''} -> ${wanted ?? ''}`)) reload();
}

/**
 * The preload could not load or verify `manifestUrl`. Drop it for this tab and
 * reload once onto the stable build. federation-config can keep handing out the
 * same preview, so it stays marked as failed for the life of the tab.
 */
export function abandonAbaxUiPreview(manifestUrl: string, reload: () => void): void {
  writeStored(null);
  const failed = readList(FAILED_KEY);
  try {
    if (!failed.includes(manifestUrl)) {
      window.sessionStorage.setItem(FAILED_KEY, JSON.stringify([...failed, manifestUrl]));
    }
  } catch {
    // Unrecorded: the reload claim below is the mark instead.
  }
  if (claimReload(`${manifestUrl}${FAILED_CLAIM}`)) reload();
}

/** Test-only: drop the remembered preview and this tab's records. */
// eslint-disable-next-line @typescript-eslint/naming-convention -- matches the repo's __internal test-seam convention.
export function __resetAbaxUiPreviewForTests(): void {
  clearAbaxUiPreview();
  window.sessionStorage.removeItem(RELOADED_KEY);
  window.sessionStorage.removeItem(FAILED_KEY);
}
